// @radish/primitives: branded value types and their pure constructors (core plan §4.2, §4.13). Zero deps.
// Arithmetic on these values lives in @radish/core/money only (invariant A4); this package only builds and checks them.

declare const brand: unique symbol;

/** Nominal tag: `string & Brand<'OrderId'>` is not assignable from a plain string. */
export type Brand<B extends string> = { readonly [brand]: B };

export type Currency = 'USD' | 'CAD';

/** Minor-unit exponent per currency (cents for both supported currencies). */
export const CURRENCY_EXPONENT: Readonly<Record<Currency, number>> = { USD: 2, CAD: 2 };

export const isCurrency = (value: string): value is Currency => value === 'USD' || value === 'CAD';

/** An amount in integer minor units (cents). */
export type Money = { readonly minor: bigint; readonly currency: Currency } & Brand<'Money'>;

/** Parts per 10^8 of the fraction: 100 % = 100_000_000n, 7.25 % = 7_250_000n, 1 bp = 10_000n. One scale, everywhere. */
export const RATE_SCALE = 10n ** 8n;
export type Rate = { readonly scaled: bigint } & Brand<'Rate'>;

/** Thousandths of a unit: 1 each = 1000n, 0.25 lb = 250n. */
export const QTY_SCALE = 1000n;
export type Qty = { readonly milli: bigint } & Brand<'Qty'>;

/** An exact rational. Invariant: `den > 0` and `gcd(|num|, den) = 1` (zero is `0/1`). Build it with `ratio()`. */
export type Ratio = { readonly num: bigint; readonly den: bigint };

export const money = (minor: bigint, currency: Currency): Money => ({ minor, currency }) as Money;
export const rate = (scaled: bigint): Rate => ({ scaled }) as Rate;
export const qty = (milli: bigint): Qty => ({ milli }) as Qty;

const abs = (n: bigint): bigint => (n < 0n ? -n : n);

/** Euclid, as a loop: ratio() calls this on every rational, and a recursive closure per call cost more than the division. */
export const gcd = (a: bigint, b: bigint): bigint => {
  let x = abs(a);
  let y = abs(b);
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
};

/** Builds a reduced ratio with a positive denominator. A zero denominator is a programming error and throws. */
export const ratio = (num: bigint, den: bigint = 1n): Ratio => {
  if (den === 0n) throw new RangeError('ratio: denominator must not be zero');
  const sign = den < 0n ? -1n : 1n;
  const g = gcd(num, den);
  const divisor = g === 0n ? 1n : g;
  return { num: (sign * num) / divisor, den: (sign * den) / divisor };
};

// Branded identifiers. Values are UUIDv7 strings minted by an injected IdGen (never inside core).
export type Id<Name extends string> = string & Brand<Name>;
export type AccountId = Id<'AccountId'>;
export type LocationId = Id<'LocationId'>;
export type DeviceId = Id<'DeviceId'>;
export type EmployeeId = Id<'EmployeeId'>;
export type CustomerId = Id<'CustomerId'>;
export type ProductId = Id<'ProductId'>;
export type OrderId = Id<'OrderId'>;
export type OrderLineId = Id<'OrderLineId'>;
/** A check (ticket) of a split order. */
export type CheckId = Id<'CheckId'>;
export type TenderId = Id<'TenderId'>;
export type RefundId = Id<'RefundId'>;
export type AdjustmentId = Id<'AdjustmentId'>;

/** Tags a string as an id of the given kind. Validation of the UUID format belongs to the contracts codec. */
export const asId = <Name extends string>(value: string): Id<Name> => value as Id<Name>;
